import { Box, Avatar, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useDarkMode } from '../../../hooks/useDarkMode';
import { NavItem } from './types';

interface NavItemStyledProps {
    isActive: boolean;
}

const SidebarRoot = styled(Box)(({ theme }) => {
    const palette = theme.customColorPalette;
    const isDarkMode = theme.palette.mode === 'dark';
    return {
        width: '47px',
        paddingTop: '24px',
        paddingBottom: '24px',
        paddingLeft: '8px',
        paddingRight: '8px',
        height: '100%',
        backgroundColor: isDarkMode ? palette.gray[900] : palette.gray[50],
        borderRight: `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[300]}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
    };
});

const UserButton = styled(Box)(({ theme }) => {
    const palette = theme.customColorPalette;
    return {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        backgroundColor: palette.blue[500],
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: palette.gray.A100,
        fontSize: '14px',
        fontWeight: 600,
        padding: 0,
        '&:hover': {
            opacity: 0.9,
        },
    };
}) as typeof Box;

const NavItemWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isActive',
})<NavItemStyledProps>(({ theme, isActive }) => {
    const palette = theme.customColorPalette;
    const isDarkMode = theme.palette.mode === 'dark';
    return {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        gap: '4px',
        padding: 0,
        border: 'none',
        width: '100%',
        ...(!isActive && {
            '&:hover': {
                '& .icon-box': {
                    backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[100],
                },
            },
        }),
    };
});

const IconBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isActive',
})<NavItemStyledProps>(({ theme, isActive }) => {
    const palette = theme.customColorPalette;
    const isDarkMode = theme.palette.mode === 'dark';
    return {
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        backgroundColor: isActive
            ? (isDarkMode ? palette.gray[800] : palette.blue[50])
            : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(!isActive && {
            '&:hover': {
                backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[100],
            },
        }),
    };
});

const NavLabel = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'isActive',
})<NavItemStyledProps>(({ theme, isActive }) => {
    const palette = theme.customColorPalette;
    const isDarkMode = theme.palette.mode === 'dark';
    return {
        fontFamily: 'Roboto',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '166%',
        letterSpacing: '0.4px',
        fontFeatureSettings: "'liga' off, 'clig' off",
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        color: isActive
            ? (isDarkMode ? palette.gray.A100 : palette.blue[500])
            : (isDarkMode ? palette.gray[400] : palette.neutral[900]),
        textAlign: 'center',
    };
});

interface CompactSidebarProps {
    navItems: NavItem[];
    activeItem: string | null;
    userAvatar?: string;
    userInitials: string;
    onNavItemClick: (path: string | null, id: string) => void;
    onUserClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const CompactSidebar: React.FC<CompactSidebarProps> = ({
    navItems,
    activeItem,
    userAvatar,
    userInitials,
    onNavItemClick,
    onUserClick,
}) => {
    const theme = useTheme();
    const { customColorPalette: palette } = theme;
    const isDarkMode = useDarkMode();

    return (
        <SidebarRoot>
            <UserButton
                component="button"
                onClick={onUserClick}
            >
                {userAvatar ? (
                    <Avatar
                        src={userAvatar}
                        sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '8px',
                        }}
                    />
                ) : (
                    userInitials
                )}
            </UserButton>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    flex: 1,
                    width: '100%',
                    alignItems: 'center',
                }}
            >
                {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeItem === item.id;

                    return (
                        <NavItemWrapper
                            key={item.id}
                            isActive={isActive}
                            onClick={() => onNavItemClick(item.path, item.id)}
                        >
                            <IconBox className="icon-box" isActive={isActive}>
                                <Icon
                                    size={20}
                                    weight="regular"
                                    color={isActive
                                        ? (isDarkMode ? palette.gray.A100 : palette.blue[500])
                                        : (isDarkMode ? palette.gray[400] : palette.neutral[900])
                                    }
                                />
                            </IconBox>
                            <NavLabel isActive={isActive}>
                                {item.label}
                            </NavLabel>
                        </NavItemWrapper>
                    );
                })}
            </Box>
        </SidebarRoot>
    );
};
